import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatarCpfMascara, apenasDigitosCpf, MSG_CPF_11_DIGITOS } from '../utils/cpf';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1F6C86 0%, #155a6e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '15px',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
  title: {
    color: '#2c3e50',
    textAlign: 'center',
    marginBottom: '10px',
    fontSize: '28px',
    fontWeight: 'bold',
  },
  subtitle: {
    color: '#666',
    textAlign: 'center',
    marginBottom: '30px',
    fontSize: '16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2c3e50',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid #e1e8ed',
    fontSize: '16px',
    width: '100%',
    transition: 'border-color 0.3s ease',
    outline: 'none',
    '&:focus': {
      borderColor: '#1F6C86',
    },
  },
  button: {
    backgroundColor: '#E0CC98',
    color: '#1F6C86',
    padding: '14px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
    marginTop: '10px',
  },
  error: {
    color: '#e74c3c',
    textAlign: 'center',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#fdf2f2',
    borderRadius: '6px',
    border: '1px solid #fecaca',
    fontSize: '14px',
  },
  forgotPassword: {
    textAlign: 'center',
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  forgotPasswordText: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '8px',
  },
  forgotPasswordLink: {
    color: '#1F6C86',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
    padding: '8px 16px',
    borderRadius: '6px',
    backgroundColor: 'white',
    border: '1px solid #1F6C86',
    display: 'inline-block',
    transition: 'all 0.3s ease',
  },
  success: {
    color: '#1b5e20',
    textAlign: 'center',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#e8f5e9',
    borderRadius: '6px',
    border: '1px solid #a5d6a7',
    fontSize: '14px',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#1F6C86',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '4px 0',
    fontFamily: 'inherit',
  },
};

function LoginPage() {
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [primeiroAcessoOpen, setPrimeiroAcessoOpen] = useState(false);
  const [cpfPrimeiroAcesso, setCpfPrimeiroAcesso] = useState('');
  const [primeiroAcessoLoading, setPrimeiroAcessoLoading] = useState(false);
  const [primeiroAcessoMessage, setPrimeiroAcessoMessage] = useState('');
  const [primeiroAcessoError, setPrimeiroAcessoError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      console.log('[DEBUG] Iniciando tentativa de login...');
      console.log('[DEBUG] CPF:', cpf);
      
      const cpfNumeros = apenasDigitosCpf(cpf);
      if (cpfNumeros.length !== 11) {
        setError(MSG_CPF_11_DIGITOS);
        return;
      }

      const response = await api.post('usuarios/login/', { 
        cpf: cpfNumeros,
        password: password 
      });
      
      console.log('[DEBUG] Resposta recebida:', response.data);
      
      if (response.data.token) {
        console.log('[DEBUG] Login bem-sucedido, armazenando dados...');
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Redireciona com base no tipo de usuário
        const loggedUser = response.data.user;
        const userType = loggedUser.tipo;
        const parqPendente = loggedUser.parq_completed !== true && loggedUser.parq_completed !== 'true';
        if (userType === 'aluno' && loggedUser.contrato_aceito === false) {
          navigate('/contrato');
        } else if (userType === 'aluno' && parqPendente) {
          navigate('/dashboard/aluno?section=parq');
        } else if (userType === 'gerente') {
          navigate('/dashboard/gerente');
        } else if (userType === 'professor') {
          navigate('/dashboard/professor');
        } else if (userType === 'aluno') {
          navigate('/dashboard/aluno');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('Erro ao realizar login. Tente novamente.');
      }
    } catch (error) {
      console.error('[DEBUG] Erro no login:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Erro ao realizar login. Tente novamente.';
      setError(errorMessage);
    }
  };

  const handlePrimeiroAcesso = async (e) => {
    e.preventDefault();
    setPrimeiroAcessoError('');
    setPrimeiroAcessoMessage('');
    const cpfNumeros = apenasDigitosCpf(cpfPrimeiroAcesso);
    if (cpfNumeros.length !== 11) {
      setPrimeiroAcessoError(MSG_CPF_11_DIGITOS);
      return;
    }
    try {
      setPrimeiroAcessoLoading(true);
      const response = await api.post('usuarios/primeiro-acesso/', { cpf: cpfNumeros });
      setPrimeiroAcessoMessage(response.data?.message || 'Solicitação registrada.');
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Não foi possível enviar. Tente novamente.';
      setPrimeiroAcessoError(typeof msg === 'string' ? msg : 'Não foi possível enviar. Tente novamente.');
    } finally {
      setPrimeiroAcessoLoading(false);
    }
  };

  return (
    <div className="login-container" style={styles.container}>
      <div className="login-card" style={styles.card}>
        <h1 className="login-title" style={styles.title}>🔐 Login</h1>
        <p className="login-subtitle" style={styles.subtitle}>
          Acesse sua conta do CT Supera
        </p>
        
        <form className="login-form" style={styles.form} onSubmit={handleLogin}>
          <div className="form-group" style={styles.inputGroup}>
            <label style={styles.label} htmlFor="cpf">
              CPF
            </label>
            <input
              id="cpf"
              type="text"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => setCpf(formatarCpfMascara(e.target.value))}
              style={styles.input}
              maxLength={14}
              inputMode="numeric"
              autoComplete="username"
              required
            />
          </div>
          
          <div className="form-group" style={styles.inputGroup}>
            <label style={styles.label} htmlFor="password">
              Senha
            </label>
            <input
              id="password"
              type="password"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
              autoComplete="current-password"
            />
          </div>
          
            <button 
              type="submit" 
              className="btn btn-primary" 
            style={styles.button}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.backgroundColor = '#D4C088';
                e.target.style.boxShadow = '0 6px 20px rgba(224, 204, 152, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.backgroundColor = '#E0CC98';
                e.target.style.boxShadow = 'none';
              }}
            >
              🚀 Entrar
            </button>
          
          {error && <div className="alert alert-danger" style={styles.error}>{error}</div>}
        </form>
        
        <div style={styles.forgotPassword}>
          <div style={styles.forgotPasswordText}>
            Esqueceu sua senha?
          </div>
          <a href="/esqueci-senha" style={styles.forgotPasswordLink}>
            🔑 Recuperar Senha
          </a>
        </div>

        <div style={{ ...styles.forgotPassword, marginTop: '16px' }}>
          <div style={styles.forgotPasswordText}>
            É aluno, já está matriculado e ainda não ativou o acesso?
          </div>
          {!primeiroAcessoOpen ? (
            <button
              type="button"
              style={styles.forgotPasswordLink}
              onClick={() => {
                setPrimeiroAcessoOpen(true);
                setPrimeiroAcessoError('');
                setPrimeiroAcessoMessage('');
              }}
            >
              Primeiro acesso — ativar conta
            </button>
          ) : (
            <form style={{ marginTop: '12px', textAlign: 'left' }} onSubmit={handlePrimeiroAcesso}>
              <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px', lineHeight: 1.45 }}>
                Informe o mesmo CPF da matrícula. Enviaremos o link de ativação para o e-mail cadastrado.
              </p>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="cpf-primeiro-acesso">
                  CPF
                </label>
                <input
                  id="cpf-primeiro-acesso"
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpfPrimeiroAcesso}
                  onChange={(e) => setCpfPrimeiroAcesso(formatarCpfMascara(e.target.value))}
                  style={styles.input}
                  maxLength={14}
                  inputMode="numeric"
                  autoComplete="username"
                  disabled={primeiroAcessoLoading}
                />
              </div>
              <button
                type="submit"
                style={{ ...styles.button, width: '100%', marginTop: '8px' }}
                disabled={primeiroAcessoLoading}
              >
                {primeiroAcessoLoading ? 'Enviando…' : '📧 Solicitar ativação de senha'}
              </button>
              {primeiroAcessoMessage && (
                <div style={styles.success} role="status">
                  {primeiroAcessoMessage}
                </div>
              )}
              {primeiroAcessoError && (
                <div className="alert alert-danger" style={{ ...styles.error, marginTop: '10px' }}>
                  {primeiroAcessoError}
                </div>
              )}
              <button
                type="button"
                style={{ ...styles.linkButton, marginTop: '12px', display: 'block', width: '100%', textAlign: 'center' }}
                onClick={() => {
                  setPrimeiroAcessoOpen(false);
                  setCpfPrimeiroAcesso('');
                  setPrimeiroAcessoMessage('');
                  setPrimeiroAcessoError('');
                }}
              >
                Voltar ao login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;